import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTerminal } from '../../hooks/useTerminal';
import TerminalCore from './TerminalCore';
import TerminalErrorBoundary from './TerminalErrorBoundary';
import { TerminalConfig } from './types';

interface TerminalProps extends TerminalConfig {
  onClose: () => void;
}

/**
 * Main Terminal component with dragging, resizing, and window management
 */
const Terminal: React.FC<TerminalProps> = ({
  sessionId,
  instanceId,
  provider = 'aws',
  instanceType,
  region,
  standalone = false,
  onClose
}) => {
  // Terminal component render
  // State for window management
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [position, setPosition] = useState(() => {
    if (standalone) return { x: 0, y: 0 };
    
    // Center the terminal window but ensure it fits on screen
    const terminalWidth = 900;
    const terminalHeight = Math.min(600, window.innerHeight - 100); // Leave space at top and bottom
    const x = Math.max(50, (window.innerWidth - terminalWidth) / 2);
    const y = Math.max(30, (window.innerHeight - terminalHeight) / 2);
    return { x, y };
  });

  // Refs
  const dragNodeRef = useRef<HTMLDivElement>(null);

  // Platform detection
  const isMac = navigator.platform.toLowerCase().includes('mac');
  const modKey = isMac ? 'Cmd' : 'Ctrl';
  const clearKey = isMac ? 'K' : 'L';

  // Use terminal hook
  const { terminalRef, state, methods } = useTerminal({
    sessionId,
    instanceId,
    provider,
    instanceType,
    region,
    standalone
  });

  // Handle window resize to fit terminal and focus management
  useEffect(() => {
    if (!isMinimized) {
      // Delay to ensure DOM is updated
      const timeout = setTimeout(() => {
        methods.focus();
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [isMinimized, isMaximized, methods]);
  
  // Special focus handling for standalone mode
  useEffect(() => {
    if (standalone && state.type === 'connected') {
      // Aggressive focus handling for standalone mode in new window
      const focusTerminal = () => {
        // First ensure the window itself has focus
        window.focus();
        // Then focus the terminal
        methods.focus();
        
        // If document doesn't have focus, try again
        if (!document.hasFocus()) {
          setTimeout(() => {
            window.focus();
            methods.focus();
          }, 100);
        }
      };
      
      // Try multiple times with increasing delays to ensure focus
      const timeouts = [100, 300, 500, 1000];
      const timers = timeouts.map(delay => 
        setTimeout(focusTerminal, delay)
      );
      
      // Also focus on any click in the window
      const handleClick = () => methods.focus();
      window.addEventListener('click', handleClick);
      
      return () => {
        timers.forEach(clearTimeout);
        window.removeEventListener('click', handleClick);
      };
    }
  }, [standalone, state.type, methods]);

  // Handle minimize
  const handleMinimize = useCallback(() => {
    const newMinimized = !isMinimized;
    setIsMinimized(newMinimized);
    
    if (newMinimized) {
      // Move to bottom-right when minimizing
      setPosition({ x: window.innerWidth - 470, y: window.innerHeight - 80 });
    } else {
      // Return to center when restoring
      const x = Math.max(50, (window.innerWidth - 900) / 2);
      const y = Math.max(30, (window.innerHeight - 600) / 2);
      setPosition({ x, y });
      // Focus terminal after restore
      setTimeout(() => methods.focus(), 300);
    }
  }, [isMinimized, methods]);

  // Handle maximize
  const handleMaximize = useCallback(() => {
    setIsMaximized(prev => !prev);
    // Focus terminal after maximize change
    setTimeout(() => methods.focus(), 300);
  }, [methods]);

  // Handle open in new window
  const handleOpenInNewWindow = useCallback(async () => {
    // Create NEW session ID for the new window
    const newSessionId = `${sessionId}-new-${Date.now()}`;
    
    const params = new URLSearchParams({
      sessionId: newSessionId,
      instanceId: instanceId || '',
      provider,
      instanceType: instanceType || '',
      region: region || '',
      createNewSession: 'true'  // Flag to create fresh SSM session
    });
    
    // First disconnect the current terminal WebSocket
    methods.disconnect();
    
    // Wait for disconnect to complete
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Now open the new window with new session
    const newWindow = window.open(
      `/terminal-window?${params.toString()}`,
      `terminal-${newSessionId}`,
      'width=1000,height=700,menubar=no,toolbar=no,location=no,status=no'
    );
    
    if (newWindow) {
      // Close the current terminal UI after opening new window
      setTimeout(() => {
        onClose();
      }, 100);
    } else {
      console.error('Failed to open terminal in new window');
      // Reconnect if window failed to open
      methods.connect();
    }
  }, [sessionId, instanceId, provider, instanceType, region, onClose, methods]);

  // Get connection indicator
  const getConnectionIndicator = useCallback(() => {
    switch (state.type) {
      case 'connecting':
        return (
          <div className="flex items-center space-x-2 text-yellow-400">
            <motion.div 
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-2 h-2 bg-yellow-400 shadow-lg shadow-yellow-400/50 rounded-full" 
            />
            <span className="text-xs font-medium">Connecting...</span>
          </div>
        );
      case 'connected':
        return (
          <div className="flex items-center space-x-2 text-green-400">
            <div className="w-2 h-2 bg-green-400 shadow-lg shadow-green-400/50 rounded-full" />
            <span className="text-xs font-medium">Connected</span>
          </div>
        );
      case 'error':
        return (
          <div className="flex items-center space-x-2 text-red-400">
            <motion.div 
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-2 h-2 bg-red-400 shadow-lg shadow-red-400/50 rounded-full" 
            />
            <span className="text-xs font-medium">Error</span>
          </div>
        );
      case 'disconnected':
        return (
          <div className="flex items-center space-x-2 text-gray-400">
            <div className="w-2 h-2 bg-gray-400 shadow-lg shadow-gray-400/50 rounded-full" />
            <span className="text-xs font-medium">Disconnected</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center space-x-2 text-gray-500">
            <div className="w-2 h-2 bg-gray-500 rounded-full" />
            <span className="text-xs font-medium">Initializing</span>
          </div>
        );
    }
  }, [state]);

  // Get provider badge
  const getProviderBadge = useCallback(() => {
    const badges = {
      aws: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30', label: 'AWS SSM' },
      gcp: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', label: 'GCP IAP' },
      azure: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500/30', label: 'Azure' }
    };

    const badge = badges[provider];
    if (!badge) return null;

    return (
      <div className={`px-2 py-1 text-xs font-medium ${badge.bg} ${badge.text} ${badge.border} border backdrop-blur-sm rounded`}>
        {badge.label}
      </div>
    );
  }, [provider]);

  // Render standalone mode (fullscreen in new window)
  if (standalone) {
    return (
      <TerminalErrorBoundary>
        <div className="h-full w-full bg-black flex flex-col">
          <div className="flex-1 relative">
            <TerminalCore 
              terminalRef={terminalRef}
              state={state}
            />
          </div>
          
          {/* Status bar */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-800/50 text-xs bg-black/90 backdrop-blur-sm">
            <div className="flex items-center space-x-4">
              {getConnectionIndicator()}
              {state.type === 'error' && (
                <div className="px-2 py-1 bg-red-500/20 text-red-400 border border-red-500/30 rounded">
                  {state.message}
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-4 text-gray-400">
              <div className="flex items-center space-x-1">
                <kbd className="px-1.5 py-0.5 bg-gray-700/50 text-xs border border-gray-600/30 rounded">Ctrl</kbd>
                <span>+</span>
                <kbd className="px-1.5 py-0.5 bg-gray-700/50 text-xs border border-gray-600/30 rounded">C</kbd>
                <span className="ml-1">Interrupt</span>
              </div>
              <div className="h-3 w-px bg-gray-700/50" />
              <div className="flex items-center space-x-1">
                <kbd className="px-1.5 py-0.5 bg-gray-700/50 text-xs border border-gray-600/30 rounded">{modKey}</kbd>
                <span>+</span>
                <kbd className="px-1.5 py-0.5 bg-gray-700/50 text-xs border border-gray-600/30 rounded">{clearKey}</kbd>
                <span className="ml-1">Clear</span>
              </div>
            </div>
          </div>
        </div>
      </TerminalErrorBoundary>
    );
  }

  // Regular draggable mode
  
  // Temporarily bypass Draggable to test Portal rendering
  const terminalElement = (
    <TerminalErrorBoundary>
      <div 
        ref={dragNodeRef}
        style={{
          position: 'fixed',
          left: isMaximized ? '2.5vw' : `${position.x}px`,
          top: isMaximized ? '5vh' : `${position.y}px`,
          width: isMinimized ? '450px' : isMaximized ? '95vw' : '900px',
          height: isMinimized ? '60px' : isMaximized ? '90vh' : `${Math.min(600, window.innerHeight - 150)}px`,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          border: '1px solid rgba(107, 114, 128, 0.3)',
          borderRadius: '12px',
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.75), 0 0 0 1px rgba(255, 255, 255, 0.05) inset',
          transition: 'all 0.3s ease-out'
        }}
        >
          {/* Header */}
          <div className="terminal-drag-handle flex items-center justify-between px-4 py-3 border-b border-gray-700/30 cursor-move bg-gradient-to-r from-gray-900/50 to-gray-800/30 backdrop-blur-sm transition-all duration-200 flex-shrink-0 hover:bg-gray-900/60">
            <div className="flex items-center space-x-4">
              {getConnectionIndicator()}
              {getProviderBadge()}
              {!isMinimized && instanceType && (
                <div className="text-xs text-gray-400 font-mono">
                  {instanceType} • {region}
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-1">
              {/* Window Controls */}
              <button
                onClick={handleOpenInNewWindow}
                className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-400/20 rounded-lg transition-all"
                title="Open in New Window"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
              
              <button
                onClick={handleMinimize}
                className="p-1.5 text-gray-400 hover:text-yellow-400 hover:bg-yellow-400/20 rounded-lg transition-all"
                title={isMinimized ? "Restore" : "Minimize"}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d={isMinimized ? "M5 15l7-7 7 7" : "M20 12H4"} />
                </svg>
              </button>
              
              <button
                onClick={handleMaximize}
                className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-400/20 rounded-lg transition-all"
                title={isMaximized ? "Restore" : "Maximize"}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMaximized ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M4 8h16v8H4z" />
                  )}
                </svg>
              </button>
              
              <button
                onClick={onClose}
                className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-400/20 rounded-lg transition-all"
                title="Close Terminal"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Terminal Container */}
          <AnimatePresence>
            {!isMinimized && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex-1 relative overflow-hidden bg-black/50"
              >
                <TerminalCore 
                  terminalRef={terminalRef}
                  state={state}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Status Bar */}
          {!isMinimized && (
            <div className="flex items-center justify-between px-4 py-2 border-t border-gray-700/30 text-xs bg-gradient-to-r from-gray-900/40 to-gray-800/20 backdrop-blur-sm flex-shrink-0">
              <div className="flex items-center space-x-4">
                {state.type === 'error' && (
                  <div className="px-2 py-1 bg-red-500/20 text-red-400 border border-red-500/30 rounded">
                    {(state as any).message || 'Connection error'}
                  </div>
                )}
                {sessionId && (
                  <div className="text-gray-500">
                    Session: {sessionId.slice(0, 8)}...
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-4 text-gray-400">
                <div className="flex items-center space-x-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-700/50 text-xs border border-gray-600/30 rounded">Ctrl</kbd>
                  <span>+</span>
                  <kbd className="px-1.5 py-0.5 bg-gray-700/50 text-xs border border-gray-600/30 rounded">C</kbd>
                  <span className="ml-1">Interrupt</span>
                </div>
                <div className="h-3 w-px bg-gray-700/50" />
                <div className="flex items-center space-x-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-700/50 text-xs border border-gray-600/30 rounded">Ctrl</kbd>
                  <span>+</span>
                  <kbd className="px-1.5 py-0.5 bg-gray-700/50 text-xs border border-gray-600/30 rounded">D</kbd>
                  <span className="ml-1">Exit</span>
                </div>
                <div className="h-3 w-px bg-gray-700/50" />
                <div className="flex items-center space-x-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-700/50 text-xs border border-gray-600/30 rounded">{modKey}</kbd>
                  <span>+</span>
                  <kbd className="px-1.5 py-0.5 bg-gray-700/50 text-xs border border-gray-600/30 rounded">{clearKey}</kbd>
                  <span className="ml-1">Clear</span>
                </div>
              </div>
            </div>
          )}
      </div>
    </TerminalErrorBoundary>
  );
  
  // Use React Portal to render at body level
  if (typeof document !== 'undefined') {
    const portal = ReactDOM.createPortal(terminalElement, document.body);
    return portal;
  }
  
  // Document not available
  return null;
};

export default Terminal;